// Pure rekenkern voor de inkoopbudget-tellers (FO-392, batch-46).
//
// Geen DB, geen Fastify, geen node:*. `InkoopBudget` houdt twee tellers bij:
//   - besteedObligo:      gecommitteerd geld uit goedgekeurde, nog niet
//                         gefactureerde bestellingen (order-BV).
//   - besteedGerealiseerd: geboekte inkoopfacturen (credits negatief).
// Beide worden event-gedreven bijgehouden en nachtelijk herrekend uit de bron.
// Deze module levert de zuivere rekenregels die het event-pad, de reconciliatie-
// cron en het backfill-script DELEN, zodat er nooit twee implementaties bestaan.
// Geld = number (euro, 2 decimalen, EXCL. btw); de service zet om van/naar
// Prisma `Decimal`. Afronden op 2 decimalen gebeurt op het eind.

import type { InkoopOrderStatusValue, InkoopFactuurStatusValue } from './inkoop-calculations'

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100
}

// ────────────────────────────────────────────────────────────────────────────
// Predikaten — welke order/factuur telt mee en hoeveel
// ────────────────────────────────────────────────────────────────────────────

/**
 * Order-statussen die obligo dragen: van goedgekeurd t/m (deels) ontvangen /
 * binnengeboekt / gefactureerd. CONCEPT en WACHT_OP_GOEDKEURING dragen nog geen
 * commitment; GEANNULEERD en AFGEROND geven het restant-obligo juist vrij
 * (AFGEROND = order expliciet afgesloten, rest-obligo vervalt).
 */
const OBLIGO_DRAGENDE_ORDERSTATUS: ReadonlySet<InkoopOrderStatusValue> = new Set([
  'GOEDGEKEURD',
  'GOEDGEKEURD_VOOR_BESTELLING',
  'BESTELD',
  'IN_BESTELLING',
  'DEELLEVERING_ONTVANGEN',
  'VOLLEDIG_ONTVANGEN',
  'ONTVANGEN',
  'BINNENGEBOEKT',
  'GEFACTUREERD',
  // batch-65 (FO-481): goedgekeurd en besteld, alleen het bestelbericht moet nog
  // (opnieuw) verstuurd worden - het geld is gecommitteerd.
  'TE_VERSTUREN',
])

export function orderDraagtObligo(status: InkoopOrderStatusValue): boolean {
  return OBLIGO_DRAGENDE_ORDERSTATUS.has(status)
}

/**
 * Telt een factuur mee in besteedGerealiseerd? Een factuur telt zodra ze voor het
 * eerst geboekt (GOEDGEKEURD) of betaald is - de markering `budgetReedsGeboekt`
 * (persistente `budgetGeboektOp`) borgt dat een terug-wissel naar IN_BEHANDELING
 * niets terugdraait (T7); alleen GEANNULEERD haalt de factuur er weer uit (T8).
 */
export function factuurTeltGerealiseerd(input: {
  status: InkoopFactuurStatusValue
  budgetReedsGeboekt: boolean
}): boolean {
  if (input.status === 'GEANNULEERD') return false
  // batch-65 factuur-richting (FO-486) — een naar verkoop verplaatste factuur
  // is geen inkoop: telt nergens mee, net als een geannuleerde.
  if (input.status === 'VERPLAATST_NAAR_VERKOOP') return false
  return input.budgetReedsGeboekt || input.status === 'GOEDGEKEURD' || input.status === 'BETAALD'
}

/**
 * Obligo-bijdrage van een order = besteld (excl. btw) minus reeds
 * (geboekt-)gefactureerd op die order, ondergrens 0 (`Decimal.max(0, ...)`-cap),
 * en 0 zodra de status geen obligo meer draagt. Zo verhuist geld van obligo naar
 * gerealiseerd naarmate er gefactureerd wordt, zonder ooit dubbel te tellen.
 */
export function orderObligoBijdrage(input: {
  status: InkoopOrderStatusValue
  orderExclBtw: number
  gefactureerdExclBtw: number
}): number {
  if (!orderDraagtObligo(input.status)) return 0
  return round2(Math.max(0, input.orderExclBtw - input.gefactureerdExclBtw))
}

// ────────────────────────────────────────────────────────────────────────────
// Teller-aggregatie (gedeeld door reconciliatie-cron + backfill)
// ────────────────────────────────────────────────────────────────────────────

export interface BudgetTellerInput {
  /** Obligo-bijdrage per aan dit budget toegerekende order (elk al op 0 gecapt). */
  orderObligoBijdragen: number[]
  /** Gerealiseerd-bijdrage per geboekte factuur toegerekend aan dit budget (credits negatief). */
  factuurGerealiseerdBijdragen: number[]
}

export interface BudgetTellers {
  besteedObligo: number
  besteedGerealiseerd: number
}

/**
 * Herrekent beide tellers uit de per-order/-factuur-bijdragen. Obligo is per order
 * al op 0 gecapt (dubbele guard hier). Gerealiseerd mag negatief zijn wanneer
 * credits het geboekte overtreffen - dat is een geldige stand, geen fout.
 */
export function herrekenBudgetTellers(input: BudgetTellerInput): BudgetTellers {
  const besteedObligo = round2(
    input.orderObligoBijdragen.reduce((s, b) => s + Math.max(0, b), 0),
  )
  const besteedGerealiseerd = round2(
    input.factuurGerealiseerdBijdragen.reduce((s, b) => s + b, 0),
  )
  return { besteedObligo, besteedGerealiseerd }
}

/** Read-only afgeleide som voor bestaande weergaven (DTO-`besteed`). */
export function budgetBesteedTotaal(t: Pick<BudgetTellers, 'besteedObligo' | 'besteedGerealiseerd'>): number {
  return round2(t.besteedObligo + t.besteedGerealiseerd)
}

/**
 * Drift = |teller - herrekend| > 1 cent op een van beide tellers. Een gemist
 * schrijfpad of een handmatig scheefgezette teller wordt hiermee zichtbaar.
 */
export function budgetDrift(
  huidig: BudgetTellers,
  herrekend: BudgetTellers,
): { obligoDrift: number; gerealiseerdDrift: number; heeftDrift: boolean } {
  const obligoDrift = round2(herrekend.besteedObligo - huidig.besteedObligo)
  const gerealiseerdDrift = round2(herrekend.besteedGerealiseerd - huidig.besteedGerealiseerd)
  const heeftDrift = Math.abs(obligoDrift) > 0.01 || Math.abs(gerealiseerdDrift) > 0.01
  return { obligoDrift, gerealiseerdDrift, heeftDrift }
}

/**
 * Bestede fractie van het budget (obligo + gerealiseerd t.o.v. bedrag). Retourneert
 * 0 bij een niet-positief bedrag (voorkomt deling door 0). Gebruikt om de 80%- en
 * 100%-drempelsignalen te bepalen; de idempotentie zit in de signaal-key, niet hier.
 */
export function budgetBesteedFractie(tellers: BudgetTellers, bedrag: number): number {
  if (bedrag <= 0) return 0
  return budgetBesteedTotaal(tellers) / bedrag
}
